import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import type { Highlight, HighlightGroup } from '@/components/pdf/types/highlight-types';
import { apiErrorMessage, backendClient } from '@/lib/api-client';
import { applyCellRangeToLocator } from '@/utils/excelCitation';

const CITATION_HIGHLIGHT_COLOR = '#173f6f';
const CITATION_HIGHLIGHT_OPACITY = 0.24;

interface SegmentCitationInfo {
  segment_index?: number;
  locator?: Record<string, unknown>;
  page_number?: number;
  chunk_start_offset?: number;
}

export type TextCitationRange = {
  start: number;
  end: number;
};

interface UseCitationTargetOptions {
  slug?: string;
  documentGuid?: string;
  chunkId?: number;
  segmentIndex?: number;
  cellRange?: string;
  initialPageNumber?: number;
}

// A citation names at most one segment, but that segment can wrap across pages,
// so it still yields a highlight per region.
export function buildCitationTargetHighlightGroups(
  segmentInfo: SegmentCitationInfo | null,
  segmentIndex?: number,
): HighlightGroup[] {
  const locator = segmentInfo?.locator || {};
  if (!segmentInfo || locator.type === 'excel') return [];

  const id = `segment-${segmentInfo.segment_index ?? segmentIndex ?? 0}`;
  const base: Omit<Highlight, 'id'> = {
    color: CITATION_HIGHLIGHT_COLOR,
    opacity: CITATION_HIGHLIGHT_OPACITY,
    isKeyHighlight: true,
  };
  const regions = Array.isArray(locator.regions)
    ? locator.regions.filter(
        (region): region is { page: number; bbox: number[] } =>
          !!region
          && typeof region === 'object'
          && typeof region.page === 'number'
          && Array.isArray(region.bbox)
      )
    : [];

  let segmentHighlights: Highlight[];
  if (regions.length > 0) {
    segmentHighlights = regions.map((region, regionIndex) => ({
      ...base,
      id: `${id}-region-${regionIndex}`,
      bbox: { page: region.page, polygon: region.bbox },
      isKeyHighlight: regionIndex === 0,
    }));
  } else if (typeof locator.page === 'number' && Array.isArray(locator.bbox)) {
    segmentHighlights = [{
      ...base,
      id,
      bbox: { page: locator.page, polygon: locator.bbox },
    }];
  } else {
    segmentHighlights = [];
  }

  if (segmentHighlights.length === 0) return [];
  return [{
    type: 'citation',
    label: 'Cited segment',
    visible: true,
    highlights: segmentHighlights,
  }];
}

export function useCitationTarget({
  slug,
  documentGuid,
  chunkId,
  segmentIndex,
  cellRange,
  initialPageNumber,
}: UseCitationTargetOptions) {
  const [pageNumber, setPageNumber] = useState<number | null>(initialPageNumber ?? null);
  const [chunkStartOffset, setChunkStartOffset] = useState<number | null>(null);
  const [excelLocator, setExcelLocator] = useState<Record<string, unknown> | null>(null);
  const [textLocator, setTextLocator] = useState<TextCitationRange | null>(null);
  const [segmentInfo, setSegmentInfo] = useState<SegmentCitationInfo | null>(null);

  useEffect(() => {
    let cancelled = false;
    setPageNumber(initialPageNumber ?? null);
    setChunkStartOffset(null);
    setExcelLocator(null);
    setTextLocator(null);
    setSegmentInfo(null);

    if ((chunkId === undefined && segmentIndex === undefined) || !slug || !documentGuid) {
      return () => { cancelled = true; };
    }

    const resolveTarget = async () => {
      try {
        const response = await backendClient.post(`/correspondence/${slug}/chunk-info`, [{
          document_guid: documentGuid,
          ...(segmentIndex !== undefined ? { segment_index: segmentIndex } : { chunk_id: chunkId }),
        }]);
        if (cancelled || !response.data?.length) return;

        const chunkInfo = response.data[0];
        // A chunk spans a whole retrieval window and often many segments, so it
        // only navigates to its page: highlighting the segments it touches would
        // claim a precision the citation lacks. Only [cite:GUID:sN] names a
        // single passage, so only it resolves to exact geometry.
        const resolvedInfo: SegmentCitationInfo | null = segmentIndex !== undefined
          ? chunkInfo
          : null;
        setSegmentInfo(resolvedInfo);

        if (chunkInfo.locator?.type === 'excel') {
          setExcelLocator(applyCellRangeToLocator(chunkInfo.locator, cellRange));
          setPageNumber(null);
          return;
        }
        if (chunkInfo.locator?.type === 'text') {
          const start = chunkInfo.locator.char_start;
          const end = chunkInfo.locator.char_end;
          if (resolvedInfo && typeof start === 'number' && typeof end === 'number') {
            setTextLocator({ start, end });
          } else if (typeof start === 'number') {
            setChunkStartOffset(start);
          }
          setPageNumber(null);
          return;
        }
        const hasExactPdfTarget = buildCitationTargetHighlightGroups(
          resolvedInfo,
          segmentIndex,
        ).length > 0;
        if (hasExactPdfTarget) {
          // Let PDFViewer's bbox target control both the page and Y position.
          // Passing initialPage as well would trigger its page-only retry scrolls,
          // which can overwrite the precise bbox scroll with Y=0.
          setPageNumber(null);
        } else if (typeof chunkInfo.page_number === 'number') {
          setPageNumber(chunkInfo.page_number);
        }
        // Only the bbox target supersedes the character offset. Segments that
        // carry a page but no geometry still need the offset to scroll within it.
        setChunkStartOffset(hasExactPdfTarget ? null : (chunkInfo.chunk_start_offset ?? null));
      } catch (error: unknown) {
        if (cancelled) return;
        console.error('Failed to resolve citation target:', error);
        toast.error(apiErrorMessage(error) || 'Could not open the cited passage.');
      }
    };

    resolveTarget();
    return () => { cancelled = true; };
  }, [
    cellRange,
    chunkId,
    documentGuid,
    initialPageNumber,
    segmentIndex,
    slug,
  ]);

  const highlightGroups = useMemo(
    () => buildCitationTargetHighlightGroups(segmentInfo, segmentIndex),
    [segmentIndex, segmentInfo],
  );

  return { pageNumber, chunkStartOffset, excelLocator, textLocator, highlightGroups };
}
